/**
 * Nápověda — mapování route → slug článku v CMS site "napoveda-{lang}".
 * Články se spravují v /admin/cms, slug článku = cesta routy bez IDs,
 * např. /obchod/invoices → "obchod-invoices".
 */

const HELP_LANGS = new Set(['cs', 'en', 'de', 'ru', 'uk']);

export function helpLang(locale: string): string {
  return HELP_LANGS.has(locale) ? locale : 'cs';
}

/** Z URL cesty udělá slug článku nápovědy. Numerické/ID segmenty zahodí. */
export function helpSlugForPath(pathname: string | null | undefined): string {
  if (!pathname || pathname === '/') return 'home';
  const segs = pathname
    .split('/')
    .filter(Boolean)
    // zahoď segmenty, které jsou čistá IDs (čísla, UUID, tokeny)
    .filter((s) => !/^\d+$/.test(s) && !/^[0-9a-f-]{20,}$/i.test(s));
  const slug = segs.join('-') || 'home';
  return SLUG_ALIASES[slug] ?? slug;
}

/** Slug aliasy — podstránky bez vlastního článku odkazují na nadřazený článek. */
const SLUG_ALIASES: Record<string, string> = {
  'crm-supervision-settings': 'crm-supervision',
  // Agendy vnořené pod období jednotky — stejné téma jako top-level článek
  'armorik-owners-periods-invoices': 'armorik-invoices',
  'armorik-owners-periods-orders': 'armorik-orders',
  'armorik-owners-periods-contracts': 'armorik-contracts',
  'armorik-owners-periods-journal': 'armorik-journal',
  'armorik-owners-periods-pre-accounting': 'armorik-pre-accounting',
  'armorik-owners-periods-vat-report': 'armorik-vat-report',
  'armorik-invoices-print': 'armorik-invoices',
  'armorik-invoices-print-issued': 'armorik-invoices',
};

export interface HelpPageItem {
  slug: string;
  title: string;
  description?: string;
}

export interface HelpPagesResponse {
  lang: string;
  pages: HelpPageItem[];
}

/** Kategorie článků na indexu /help — [id, i18n klíč, ikona]. */
export const HELP_CATEGORIES: [string, string, string][] = [
  ['general', 'helpCatGeneral', '🧭'],
  ['calendar', 'helpCatCalendar', '📅'],
  ['obchod', 'helpCatObchod', '🧾'],
  ['crm', 'helpCatCrm', '🤝'],
  ['communication', 'helpCatCommunication', '💬'],
  ['payments', 'helpCatPayments', '💳'],
  ['armorik', 'helpCatArmorik', '📒'],
  ['other', 'helpCatOther', '📚'],
];

const SLUG_CATEGORY: Record<string, string> = {
  'obecne-ovladani': 'general', home: 'general',
  calendar: 'calendar', tasks: 'calendar', meetings: 'calendar', calls: 'calendar',
  'obchod-invoices': 'obchod', 'obchod-orders': 'obchod', 'obchod-products': 'obchod',
  'obchod-brands': 'obchod', 'obchod-analytics': 'obchod', accounts: 'obchod',
  'accounts-duplicity': 'obchod', 'contacts-duplicity': 'obchod',
  contacts: 'obchod', consumption: 'obchod', vincis: 'obchod',
  crm: 'crm', 'crm-leads': 'crm', 'crm-cases': 'crm',
  'crm-opportunities': 'crm', 'crm-activities': 'crm', 'crm-proces': 'crm',
  'crm-concept': 'crm', 'crm-supervision': 'crm', 'crm-contact-sources': 'crm',
  email: 'communication', chat: 'communication', 'in-chat': 'communication',
  sms: 'communication', notes: 'communication', news: 'communication', links: 'communication',
  documents: 'communication',
  payments: 'payments', devices: 'payments', 'payment-requests': 'payments',
  'create-payment': 'payments', credit: 'payments',
};

export function helpCategory(slug: string): string {
  if (slug.startsWith('armorik-')) return 'armorik';
  // Mzdy jsou zapuštěné v účetní jednotce — řadí se do účetní kategorie
  if (slug === 'payroll' || slug.startsWith('payroll-')) return 'armorik';
  return SLUG_CATEGORY[slug] ?? 'other';
}
